// components/RequireStoreValue.tsx
import {
  useCheckAuthorizationStatusQuery,
  useGetUsersMarketplaces,
} from '@sellsen/services';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { useProductMarketStore } from '../product-market-select/state';
import { resolveMarketplaceParam } from './marketplace-param';

type RequireStoreValueProps = {
  children: React.ReactNode;
};

export function RequireStoreValue({ children }: RequireStoreValueProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isOnProductMarketSelectPage = location.pathname === '/product-market-select';

  const { data: userMarketplaces, isLoading: isUserMarketPlacesLoading } =
    useGetUsersMarketplaces();
  const {
    data: authorizationStatuses,
    isLoading: isAuthorizationStatusesLoading,
  } = useCheckAuthorizationStatusQuery();

  const { selectedCountry, setSelectedCountry } = useProductMarketStore(
    useShallow((state) => ({
      selectedCountry: state.selectedCountry,
      setSelectedCountry: state.setSelectedCountry,
    }))
  );

  // Derin bağlantı (eklentinin Seller Central rozeti): `?marketplace=…` seçili pazarı belirler.
  // Böylece yeni sekmede pazar seçim sayfasına düşülmez; farklı pazar seçiliyken de doğru
  // pazara geçilir. Yalnız kullanıcının kendi pazarları kabul edilir.
  const requestedMarketplace = useMemo(
    () => resolveMarketplaceParam(searchParams.get('marketplace'), userMarketplaces),
    [searchParams, userMarketplaces]
  );
  const marketplaceSwitchPending =
    Boolean(requestedMarketplace) && requestedMarketplace !== selectedCountry;

  useEffect(() => {
    if (marketplaceSwitchPending && requestedMarketplace) {
      setSelectedCountry(requestedMarketplace);
    }
  }, [marketplaceSwitchPending, requestedMarketplace, setSelectedCountry]);

  // Memoize the loading state to prevent unnecessary re-renders
  const isLoading = useMemo(
    () => isUserMarketPlacesLoading || isAuthorizationStatusesLoading,
    [isUserMarketPlacesLoading, isAuthorizationStatusesLoading]
  );

  // Memoize navigation conditions to prevent unnecessary re-evaluations
  const shouldRedirectToNewMarketplace = useMemo(
    () =>
      authorizationStatuses?.some((status) => status.isAuthorized) &&
      userMarketplaces?.length === 0,
    [authorizationStatuses, userMarketplaces]
  );

  const shouldRedirectToOAuth = useMemo(
    () =>
      authorizationStatuses &&
      authorizationStatuses.length > 0 &&
      authorizationStatuses.every((status) => !status.isAuthorized),
    [authorizationStatuses]
  );

  const shouldRedirectToProductMarketSelect = useMemo(
    () =>
      !isOnProductMarketSelectPage &&
      !requestedMarketplace &&
      !selectedCountry &&
      !isUserMarketPlacesLoading &&
      userMarketplaces &&
      userMarketplaces.length > 0 &&
      authorizationStatuses?.some((status) => status.isAuthorized),
    [
      isOnProductMarketSelectPage,
      requestedMarketplace,
      selectedCountry,
      isUserMarketPlacesLoading,
      userMarketplaces,
      authorizationStatuses,
    ]
  );

  // Pazar adresteki değere geçene kadar çocuk çizilmez: detay sayfası eski pazarla sorgu atıp
  // bir anlık "Sipariş bulunamadı" göstermesin.
  if (isLoading || marketplaceSwitchPending) {
    return <div>Loading...</div>;
  }

  if (shouldRedirectToNewMarketplace) {
    return (
      <Navigate
        to="/new-marketplace"
        replace
        state={{ from: location }} // keep info about where user came from
      />
    );
  }

  if (shouldRedirectToOAuth) {
    return (
      <Navigate
        to="/oauth-management"
        replace
        state={{ from: location }} // keep info about where user came from
      />
    );
  }

  if (shouldRedirectToProductMarketSelect) {
    return (
      <Navigate
        to="/product-market-select"
        replace
        state={{ from: location }} // keep info about where user came from
      />
    );
  }

  return children;
}
